export default {
  article: {
    metaTitle: "Guide Basetao Spreadsheet 2026 : comment organiser vos achats",
    metaDescription:
      "Apprenez à créer et utiliser une feuille de calcul Basetao pour organiser les liens produits, les vendeurs, les photos QC et les détails d'expédition au même endroit.",
    breadcrumbHome: "Accueil",
    breadcrumbCurrent: "Guide Basetao Spreadsheet",
    title: "Le guide complet Basetao Spreadsheet (2026)",
    subtitle:
      "Un guide pratique pour créer et utiliser une feuille de calcul Basetao afin d'organiser les liens produits, les vendeurs, les photos QC et les détails d'expédition.",
    authorLabel: "Par",
    author: "Équipe Basetao Spreadsheet",
    updatedLabel: "Dernière mise à jour",
    updated: "Août 2026",
    readingTime: "8 min de lecture",
    tocLabel: "Sur cette page",
    intro: {
      heading: "Introduction",
      p1: "Faire ses achats via un agent d'achat chinois comme Basetao ouvre la porte à une immense gamme de produits — des baskets et vêtements aux accessoires et essentiels du quotidien. Mais dès que votre panier commence à se remplir, suivre chaque article, vendeur, prix et photo QC peut vite devenir écrasant.",
      p2: "C'est là qu'intervient une feuille de calcul Basetao. C'est une façon simple et organisée de noter tout ce que vous achetez, pour toujours savoir ce que vous avez commandé, où cela se trouve et combien cela a coûté. Ce guide vous explique tout ce qu'il faut pour en créer et utiliser une efficacement.",
    },
    whatIsBasetao: {
      heading: "Qu'est-ce que Basetao ?",
      p1: "Basetao est un agent d'achat qui aide les acheteurs internationaux à acheter des articles sur les plateformes chinoises telles que Taobao, 1688 et Weidian. Il agit comme intermédiaire : vous collez un lien produit, l'agent achète l'article pour vous, le stocke dans son entrepôt puis l'expédie vers votre pays. En chemin, vous pouvez demander des photos QC (contrôle qualité) pour confirmer que l'article est conforme avant son expédition.",
    },
    whatIsSpreadsheet: {
      heading: "Qu'est-ce qu'une feuille de calcul Basetao ?",
      p1: "Une feuille de calcul Basetao est un document partagé — généralement créé dans Google Sheets ou Excel — qui liste chaque produit qui vous intéresse ou que vous avez commandé via l'agent. Elle note généralement le lien produit, le vendeur, le prix, la taille ou la couleur, le statut QC et l'avancement de l'expédition.",
      p2: "Considérez-la comme votre centre de contrôle personnel pour un achat Basetao. Au lieu de jongler avec des dizaines d'onglets de navigateur et de messages, vous gardez tout au même endroit que vous pouvez mettre à jour et consulter d'un coup d'œil.",
    },
    why: {
      heading: "Pourquoi utiliser une feuille de calcul Basetao ?",
      p1: "Une feuille bien tenue fait gagner du temps et évite des erreurs coûteuses. Voici les principaux avantages :",
      items: [
        "Suivre de nombreux articles à la fois — particulièrement utile pour les gros achats.",
        "Comparer les prix et les vendeurs avant de vous engager sur un achat.",
        "Noter les résultats QC pour décider rapidement de garder ou renvoyer un article.",
        "Surveiller le statut d'entrepôt et d'expédition sans fouiller les journaux de discussion.",
        "Calculer un coût total réaliste, y compris les frais d'agent et l'expédition.",
      ],
    },
    whatToInclude: {
      heading: "Que faut-il inclure dans votre feuille de calcul ?",
      p1: "Les colonnes exactes dépendent de votre façon d'acheter, mais ces champs sont utiles pour presque tout le monde :",
      items: [
        "Lien produit — l'URL d'origine pour rouvrir l'annonce à tout moment.",
        "Nom du vendeur — pour comparer la qualité entre différentes boutiques.",
        "Prix — en devise locale et dans votre devise.",
        "Taille, couleur et quantité — pour éviter de commander la mauvaise variante.",
        "Statut QC et lien photo — pour suivre les articles qui ont passé l'inspection.",
        "Statut d'entrepôt — arrivé, stocké ou prêt à expédier.",
        "Méthode d'expédition et numéro de suivi — pour la livraison finale.",
      ],
    },
    structure: {
      heading: "Comment structurer votre feuille de calcul ?",
      p1: "Une disposition de colonnes claire rend votre feuille facile à parcourir et à filtrer. Une structure de départ pratique ressemble à ceci :",
      columns: [
        { col: "Nom du produit", desc: "Un nom court et clair pour retrouver rapidement les articles." },
        { col: "Lien produit", desc: "L'URL complète pour rouvrir l'annonce si besoin." },
        { col: "Vendeur", desc: "Le nom de la boutique, utile pour comparer la qualité." },
        { col: "Prix", desc: "Prix local et prix converti dans votre devise." },
        { col: "Taille / couleur", desc: "La variante exacte que vous prévoyez d'acheter." },
        { col: "QC", desc: "Un rapide oui/non ou un lien vers les photos QC." },
        { col: "Statut", desc: "Commandé, en entrepôt ou expédié." },
      ],
    },
    tips: {
      heading: "Conseils pour rester organisé",
      items: [
        "Utilisez une ligne par article pour filtrer et trier facilement.",
        "Codez les lignes par couleur selon le statut pour voir l'avancement d'un coup d'œil.",
        "Gardez le lien produit d'origine — les annonces peuvent changer ou disparaître.",
        "Mettez à jour la feuille juste après chaque action au lieu de vous fier à la mémoire.",
        "Sauvegardez la feuille ou partagez-la avec vous-même dans le cloud.",
      ],
    },
    mistakes: {
      heading: "Erreurs courantes à éviter",
      items: [
        "Sauter le QC et ne découvrir les problèmes qu'après l'expédition de l'article.",
        "Oublier de noter la taille ou la couleur exacte, ce qui mène à de mauvaises commandes.",
        "Ne pas suivre les frais d'expédition, ce qui rend le total final surprenant.",
        "Utiliser des noms de produits peu clairs, impossibles à retrouver plus tard.",
        "Laisser la feuille se périmer, de sorte qu'elle ne reflète plus la réalité.",
      ],
    },
    faq: {
      heading: "Questions fréquentes",
      items: [
        {
          q: "Une feuille de calcul Basetao est-elle identique au site officiel Basetao ?",
          a: "Non. C'est un outil d'organisation indépendant. Elle n'est ni affiliée ni liée au site officiel Basetao, sauf indication contraire explicite.",
        },
        {
          q: "Ai-je besoin d'une feuille de calcul pour une petite commande ?",
          a: "Pas strictement, mais même quelques articles deviennent plus faciles à gérer lorsque les liens, les prix et le statut QC sont tous au même endroit.",
        },
        {
          q: "Quel outil devrais-je utiliser pour créer la feuille de calcul ?",
          a: "Google Sheets et Excel sont tous deux populaires. Google Sheets est facile à consulter depuis n'importe quel appareil et simple à partager.",
        },
        {
          q: "Que faire si une annonce produit disparaît ?",
          a: "Gardez le nom du vendeur et une description claire dans votre feuille, et contactez l'agent avec le lien d'origine si vous l'avez encore.",
        },
        {
          q: "Ce site vend-il des produits directement ?",
          a: "Non. Ce site est une ressource d'information. Les catégories de produits renvoient vers le site principal où vous pouvez parcourir et acheter.",
        },
      ],
    },
    cta: {
      title: "Prêt à organiser votre prochain achat Basetao ?",
      desc: "Explorez les catégories de produits sélectionnées ou rendez-vous sur le site principal pour parcourir tout le catalogue.",
    },
  },
};